import { ContractDatesEnum, ContractNumbersEnum, ContractSectionsObject, ContractSectionsText, ContractStatus, ContractDeltaStatus, } from '../models/Contracts';
import DateUtils from './DateUtils';
export const contractDatesMappingValuesUtil = {
    [ContractDatesEnum.EXECUTION_DATE]: 'Execution Date',
    [ContractDatesEnum.START_DATE]: 'Start Date',
    [ContractDatesEnum.COMPLETION_DATE]: 'Completion Date',
    [ContractDatesEnum.NOTICE_TO_PROCEED_DATE]: 'Notice to Proceed Date',
    [ContractDatesEnum.ESTIMATED_COMPENSATION_DATE]: 'Estimated Compensation Date',
    [ContractDatesEnum.SUBSTANTIAL_COMPLETION_DATE]: 'Substantial Completion Date',
};
export const contractNumbersMappingValuesUtil = {
    [ContractNumbersEnum.ARCHITECTURAL_NUMBER]: 'Architectural Number',
    [ContractNumbersEnum.PUBLIC_WORKS_PROJECT_NUMBER]: 'Public Works Project Number',
    [ContractNumbersEnum.BID_NUMBER]: 'Bid Number',
};
export const contractNumbersReferenceTextUtil = {
    [ContractNumbersEnum.ARCHITECTURAL_NUMBER]: 'Arch-',
    [ContractNumbersEnum.PUBLIC_WORKS_PROJECT_NUMBER]: 'PWP-',
    [ContractNumbersEnum.BID_NUMBER]: 'Bid-',
};
export const contractScheduleOfValuesMappingValues = {
    cost_code: 'Cost Code',
    cost_description: 'Cost Description',
    description: 'Description',
    amount: 'Amount',
};
export const contractSectionsTitlesUtil = {
    [ContractSectionsText.DESCRIPTION]: 'Description',
    [ContractSectionsText.GENERAL_TERMS]: 'General Terms',
    [ContractSectionsText.INCLUSIONS]: 'Inclusions',
    [ContractSectionsText.EXCLUSIONS]: 'Exclusions',
    [ContractSectionsObject.DETAILS]: 'Details',
    [ContractSectionsObject.SCHEDULE_OF_VALUES]: 'Schedule of Values',
    [ContractSectionsObject.CONTRACT_ADDENDUMS]: 'Contract Documents',
};
export const contractStatusTitlesUtil = {
    [ContractStatus.DRAFT]: 'Draft',
    [ContractStatus.REVIEW]: 'Review',
    [ContractStatus.APPROVAL]: 'Approval',
    [ContractStatus.SIGNING]: 'Signing',
    [ContractStatus.SIGNING_IN_PROCESS]: 'Waiting for Signatures',
    [ContractStatus.COMPLETE]: 'Complete',
    [ContractStatus.SEALING]: 'Sealing',
    [ContractStatus.TERMINATED]: 'Terminated',
    [ContractStatus.VOIDED]: 'Voided',
};
export class IndividualContractSharedUtils {
    static CONTRACT_ARRAY_OMIT_KEY = 'omit';
    static createPatchesMapUtil = (patches) => {
        const patchesMap = new Map();
        patches?.forEach((patch) => {
            patch.forEach((innerPatch) => {
                const key = innerPatch?.word_count;
                if (!patchesMap.has(key)) {
                    patchesMap.set(key, []);
                }
                patchesMap.get(key).push(innerPatch);
            });
        });
        return patchesMap;
    };
    static createPatchesMapWithStatusUtil = (patchesWithStatus) => {
        const patchesMap = new Map();
        patchesWithStatus?.forEach(({ diff, status }) => {
            diff.forEach((innerPatch) => {
                const key = innerPatch?.word_count;
                if (!patchesMap.has(key)) {
                    patchesMap.set(key, []);
                }
                patchesMap.get(key).push({ ...innerPatch, status });
            });
        });
        return patchesMap;
    };
    static getDetailsToCompareUtil = (contractInfo) => {
        return {
            dates: contractInfo?.dates,
            numbers: contractInfo?.numbers,
            contract_number: contractInfo?.contract_number,
            standard_retainage: contractInfo?.standard_retainage,
        };
    };
    static getScheduleOfValuesToCompareUtil = (contractScheduleOfValues) => {
        return {
            content: contractScheduleOfValues?.content,
        };
    };
    static getAddendumsToCompareUtil = (contractDocuments) => {
        return {
            content: contractDocuments?.content,
        };
    };
    static applyPatchesToSection(section, sectionInitialText, documents, highlights = false) {
        const contractPatcher = new SharedTextPatcherUtil();
        const filteredDocs = documents
            ?.filter((doc) => doc.section_id === section)
            .sort((a, b) => DateUtils.sortByDate(a.created_date || '', b.created_date || '', false));
        return typeof sectionInitialText === 'string'
            ? this.regularOrNormalHtmlPatches(highlights, sectionInitialText, filteredDocs)
            : contractPatcher.applyJsonPatches(sectionInitialText ?? {}, filteredDocs);
    }
    static regularOrNormalHtmlPatches(highlights, sectionInitialText, documents) {
        const contractPatcher = new SharedTextPatcherUtil();
        return highlights
            ? contractPatcher.applyHtmlPatchesWithStatus(sectionInitialText ?? '', documents)
            : contractPatcher.applyHtmlPatches(sectionInitialText ?? '', documents);
    }
}
export class SharedTextPatcherUtil {
    tokenizer;
    constructor(locale = 'en') {
        // Create HTML-aware tokenizer that treats HTML tags as atomic units
        const htmlAwareTokenizer = {
            segment: (text) => {
                const tokens = [];
                const htmlTagRegex = /<[^>]+>/g;
                let lastIndex = 0;
                let match;
                // Find all HTML tags
                while ((match = htmlTagRegex.exec(text)) !== null) {
                    // Add text before the tag (tokenize by word)
                    if (match.index > lastIndex) {
                        const textBefore = text.slice(lastIndex, match.index);
                        const wordSegmenter = new Intl.Segmenter(locale, { granularity: 'word' });
                        for (const { segment, isWordLike } of wordSegmenter.segment(textBefore)) {
                            tokens.push({ segment, isWordLike: isWordLike ?? false });
                        }
                    }
                    // Add the HTML tag as a single atomic token
                    tokens.push({ segment: match[0], isWordLike: false });
                    lastIndex = match.index + match[0].length;
                }
                // Add remaining text after last tag
                if (lastIndex < text.length) {
                    const textAfter = text.slice(lastIndex);
                    const wordSegmenter = new Intl.Segmenter(locale, { granularity: 'word' });
                    for (const { segment, isWordLike } of wordSegmenter.segment(textAfter)) {
                        tokens.push({ segment, isWordLike: isWordLike ?? false });
                    }
                }
                return tokens;
            },
            resolvedOptions: () => ({
                locale,
                granularity: 'word',
            }),
        };
        this.tokenizer = htmlAwareTokenizer;
    }
    segmentText(text) {
        const rawParts = [...this.tokenizer.segment(text)].map((segment) => segment.segment);
        var tokens = [];
        var prevPart = null;
        rawParts.forEach(function (part) {
            if (/\s/.test(part)) {
                if (prevPart == null) {
                    tokens.push(part);
                }
                else {
                    tokens.push(tokens.pop() + part);
                }
            }
            else if (/\s/.test(prevPart)) {
                if (tokens[tokens.length - 1] == prevPart) {
                    tokens.push(tokens.pop() + part);
                }
                else {
                    tokens.push(prevPart + part);
                }
            }
            else {
                tokens.push(part);
            }
            prevPart = part;
        });
        return tokens;
    }
    applyHtmlPatches(originalText, docs) {
        const approvedDiffs = docs.map((doc) => doc.diff);
        const patches = IndividualContractSharedUtils.createPatchesMapUtil(approvedDiffs);
        let words = this.segmentText(originalText);
        const sortedPatches = Array.from(patches.entries()).sort(([a], [b]) => b - a);
        for (const [index, patchList] of sortedPatches) {
            const removals = patchList.filter((p) => p.removed);
            for (const removal of removals) {
                const removeWords = this.segmentText(removal.value);
                words.splice(index, removeWords.length);
            }
            const additions = patchList.filter((p) => p.added);
            for (const addition of additions) {
                const addWords = this.segmentText(addition.value);
                words.splice(index, 0, ...addWords);
            }
        }
        return words.join('').replace(/\s+/g, ' ');
    }
    applyHtmlPatchesWithStatus(originalText, docs) {
        const patchesWithStatusDoc = docs?.map((doc) => ({
            diff: doc.diff,
            status: doc.approved,
        })) ?? [];
        const patches = IndividualContractSharedUtils.createPatchesMapWithStatusUtil(patchesWithStatusDoc);
        let words = this.segmentText(originalText);
        const sortedPatches = Array.from(patches.entries()).sort(([a], [b]) => b - a);
        for (const [index, patchList] of sortedPatches) {
            const removals = patchList.filter((p) => p.removed);
            for (const removal of removals) {
                const removeWords = this.segmentText(removal.value);
                if (removal.status === ContractDeltaStatus.PENDING) {
                    // Highlight pending removals
                    const highlightedRemoval = this.highlightDelta(removal.value, 'rejected');
                    const highlightedWords = this.segmentText(highlightedRemoval);
                    words.splice(index, removeWords.length, ...highlightedWords);
                }
                else if (removal.status === ContractDeltaStatus.APPROVED) {
                    // Remove approved removals
                    words.splice(index, removeWords.length);
                }
            }
            const additions = patchList.filter((p) => p.added);
            for (const addition of additions) {
                if (addition.status === ContractDeltaStatus.PENDING) {
                    // Highlight pending additions
                    const highlightedAddition = this.highlightDelta(addition.value, 'approved');
                    const addWords = this.segmentText(highlightedAddition);
                    words.splice(index, 0, ...addWords);
                }
                else if (addition.status === ContractDeltaStatus.APPROVED) {
                    // Add approved additions without highlighting
                    const addWords = this.segmentText(addition.value);
                    words.splice(index, 0, ...addWords);
                }
            }
        }
        const returnWords = words.join('').replace(/\s+/g, ' ');
        return returnWords;
    }
    highlightDelta(deltaValue, className) {
        const htmlTagRegex = /<[^>]+>/g;
        const hasHtmlTags = htmlTagRegex.test(deltaValue);
        if (!hasHtmlTags) {
            // No HTML tags - wrap the entire text in a span
            return `<span class="${className}">${deltaValue}</span>`;
        }
        // Has HTML tags - wrap text nodes with spans while preserving HTML structure
        let result = '';
        let lastIndex = 0;
        let match;
        // Reset regex
        htmlTagRegex.lastIndex = 0;
        while ((match = htmlTagRegex.exec(deltaValue)) !== null) {
            // Get text before this tag
            const textBefore = deltaValue.slice(lastIndex, match.index);
            // Wrap text nodes with span (but skip if it's just whitespace)
            if (textBefore && textBefore.trim()) {
                result += `<span class="${className}">${textBefore}</span>`;
            }
            else if (textBefore) {
                result += textBefore;
            }
            // Add the HTML tag as-is
            result += match[0];
            lastIndex = match.index + match[0].length;
        }
        // Handle any remaining text after the last tag
        const textAfter = deltaValue.slice(lastIndex);
        if (textAfter && textAfter.trim()) {
            result += `<span class="${className}">${textAfter}</span>`;
        }
        else if (textAfter) {
            result += textAfter;
        }
        return result;
    }
    applyJsonPatches(originalText, documents) {
        const approvedDiffs = documents.map((doc) => doc.diff);
        const patches = IndividualContractSharedUtils.createPatchesMapUtil(approvedDiffs);
        let words = this.segmentJson(originalText);
        const sortedPatches = Array.from(patches.entries()).sort(([a], [b]) => b - a);
        if (!sortedPatches) {
            return words.join('');
        }
        for (const [index, patchList] of sortedPatches) {
            const removals = patchList.filter((p) => p.removed);
            for (const removal of removals) {
                words.splice(index, removal.count);
            }
            const additions = patchList.filter((p) => p.added);
            for (const addition of additions) {
                const addWords = addition.value.split(/(\n)/);
                words.splice(index, 0, ...addWords);
            }
        }
        return words.join('');
    }
    segmentJson(object) {
        const orderedObject = this.orderKeyAscending(object);
        const prettyJson = JSON.stringify(orderedObject, null, 2);
        return prettyJson.split(/(\n)/);
    }
    orderKeyAscending(obj) {
        if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) {
            return obj;
        }
        const ordered = {};
        const keys = Object.keys(obj).sort((a, b) => a.localeCompare(b));
        for (const key of keys) {
            ordered[key] = this.orderKeyAscending(obj[key]);
        }
        return ordered;
    }
}
export class SignContractSharedUtils {
    static genrateContractHtml(signaturesDocs, title, contractInfo, contractDescription, generalTerms, inclusions, exclusions, scheduleOfValues, baseUrl, projectId, contractAddendums = []) {
        const addendumsHtml = SignContractSharedUtils.getAddendumsUtil(baseUrl, projectId, contractAddendums);
        const finalHtml = `<h1>${title}</h1><p></p>${SignContractSharedUtils.getContractInfoText(contractInfo)}<p></p><h2>Description</h2> ${contractDescription}<p></p><h2>General Terms</h2> ${generalTerms}<p></p><h2>Inclusions</h2> ${inclusions}<p></p><h2>Exclusions</h2> ${exclusions}<p class="table-sections-divider" style="display: none;"></p><p></p>${SignContractSharedUtils.getScheduleOfValuesText(scheduleOfValues)}<p></p>${SignContractSharedUtils.getSignatureFieldsUtil(SignContractSharedUtils.getSignaturesForHtml(signaturesDocs))}<p></p>${addendumsHtml ? `<p class="contract-addendums-section">${addendumsHtml}</p>` : ''}
    `;
        return finalHtml;
    }
    static getSignaturesForHtml(signaturesDoc) {
        const signerInfo = signaturesDoc?.map((signatureDoc) => {
            const signersInformation = {
                name: signatureDoc.signer_name,
                role: signatureDoc.signer_role,
                signed: !!signatureDoc.signed_date && !!signatureDoc.signer_ip_address,
                date: DateUtils.format(new Date(signatureDoc.signed_date)),
            };
            return signersInformation;
        });
        return signerInfo;
    }
    static getContractInfoText(contractInfo) {
        let html = '';
        const dates = Object.keys(contractInfo.dates).filter((key) => !!contractInfo.dates[key]);
        const numbers = Object.keys(contractInfo.numbers).filter((key) => !!contractInfo.numbers[key]);
        if (dates.length > 0) {
            dates.forEach((key) => {
                html += `<p><span><strong>${contractDatesMappingValuesUtil[key]}</strong>: ${DateUtils.format(new Date(contractInfo.dates[key]))}</span></p>`;
            });
        }
        if (numbers.length > 0) {
            numbers.forEach((key) => {
                html += `<p><span><strong>${contractNumbersMappingValuesUtil[key]}</strong>: ${contractInfo.numbers[key]}</span></p>`;
            });
        }
        if (contractInfo.standard_retainage) {
            html += `<p><span><strong>Standard Retainage</strong>: ${contractInfo.standard_retainage}%</span></p>`;
        }
        if (contractInfo.contract_number) {
            html += `<p><span><strong>Contract Number</strong>: ${contractInfo.contract_number}</span></p>`;
        }
        return html;
    }
    static getDescriptionText(descriptionToRender) {
        return `<h2>Description</h2> ${descriptionToRender}`;
    }
    static getGeneralTermsText(generalTermsToRender) {
        return `<h2>General Terms</h2> ${generalTermsToRender}`;
    }
    static getInclusionsAndExclusionsText(inclusionsAndExclusionsToRender) {
        return `<h2>Inclusions and Exclusions</h2> ${inclusionsAndExclusionsToRender}`;
    }
    static getScheduleOfValuesText(inclusionsAndExclusionsToRender) {
        if (inclusionsAndExclusionsToRender.length === 0) {
            return '';
        }
        const calculateParentAmount = (row) => {
            if (row && row.children && row.children.length > 0) {
                return row.children.reduce((sum, child) => sum + (child.amount || 0), 0);
            }
            return row.amount || 0;
        };
        const renderRow = (row, isChild = false) => {
            const displayAmount = isChild ? row.amount || 0 : calculateParentAmount(row);
            const costCodeCell = isChild ? row.cost_code : `${row.cost_code}\n${row.cost_description}`;
            return `<table class="schedule-of-values-table" border="1" cellpadding="5" cellspacing="0" style="border-collapse: collapse; width: 100%; margin: 0;">
        <tr style="${isChild ? 'background-color: #f9f9f9;' : ''}">
          <td style="width: 20%; text-align: left; ${isChild ? '' : 'white-space: pre-line'}; ${isChild ? 'padding-left: 20px;' : ''}">${costCodeCell}</td>
          <td style="width: 60%;">${row.description}</td>
          <td style="width: 20%; text-align: right;">$${displayAmount.toLocaleString()}</td>
        </tr>
      </table>
    `;
        };
        const headerTable = `
      <table border="1" class="schedule-of-values-table" cellpadding="5" cellspacing="0" style="border-collapse: collapse; width: 100%; margin: 0;">
        <tr style="background-color: #e9e9e9; font-weight: bold;">
          <td style="width: 20%;">Cost Code</td>
          <td style="width: 60%;">Cost Description</td>
          <td style="width: 20%; text-align: right;">Amount</td>
        </tr>
      </table>
    `;
        const tableRows = inclusionsAndExclusionsToRender
            .map((row) => {
            let html = renderRow(row);
            if (row.children && row.children.length > 0) {
                html += row.children.map((child) => renderRow(child, true)).join('');
            }
            return html;
        })
            .join('');
        const totalAmount = inclusionsAndExclusionsToRender.reduce((sum, row) => {
            return sum + calculateParentAmount(row);
        }, 0);
        const totalRow = `
      <table class="schedule-of-values-table" border="1" cellpadding="5" cellspacing="0" style="border-collapse: collapse; width: 100%; margin: 0;">
        <tr style="background-color: #d4edda; font-weight: bold;">
          <td style="width: 20%;"></td>
          <td style="width: 60%;">TOTAL</td>
          <td style="width: 20%; text-align: right;">$${totalAmount.toLocaleString()}</td>
        </tr>
      </table>
    `;
        return `
      <h2>Schedule of Values</h2>
          ${headerTable}
          ${tableRows}
          ${totalRow}
    `;
    }
    static getAddendumsUtil = (baseUrl, projectId, addendums) => {
        if (addendums.length === 0) {
            return '';
        }
        let html = `
      <h2>CONTRACT DOCUMENTS</h2><p>The contract documents listed below are legally binding and constitute an integral part of this agreement. By signing this page, all parties acknowledge and agree to the terms and conditions set forth in this contract and the referenced documents.</p>
    `;
        addendums.forEach((doc) => {
            const fileName = doc.name || 'Untitled Document';
            const date = doc.created_date ? DateUtils.formatIso(doc.created_date) : '';
            const documentUrl = `${baseUrl}/#/project/${projectId}/documents/view/table/${doc._id}`;
            html += `<p><a href="${documentUrl}" style="color: #0066cc; text-decoration: underline;">${fileName}</a>${date ? `<span style="margin-left: 100px;">&nbsp; &nbsp; ${date}</span>` : ''}</p>`;
        });
        return html;
    };
    static getSignatureFieldsUtil = (signers) => {
        let html = '';
        for (let i = 0; i < signers.length; i += 2) {
            html += `
    <table border="0" cellpadding="0" cellspacing="0" width="100%" style="border:none">
      <tr>`;
            for (let j = 0; j < 2; j++) {
                const signerIndex = i + j;
                if (signerIndex < signers.length) {
                    const signer = signers[signerIndex];
                    html += `
        <td width="30%" style="border:none">
          <br><span><b>${signer.role ? signer.role : '&nbsp;'}</b></span>
          <br>${signer.name}
          <br>Company
          <br>Signature:
          <br>
          <br><span class="ql-font-carette" style="display: inline-block; width: 60%; border-bottom: 1px solid black"><br>${signer.signed ? signer.name : '&nbsp;'}</span>
          <br>${signer.signed ? signer.date : '&nbsp;'}
        </td>`;
                }
                else {
                    html += `
         <td width="30%" style="border:none">
          &nbsp;
         </td>
        `;
                }
            }
            html += `
      </tr>
    </table>`;
        }
        return html;
    };
}
